import {
  getItemIndex,
  getItemQty,
  getLineItem,
  getLineItemId,
  showErrorMsg,
} from "@Components/cart/helpers/helpers";
import { MEMBERSHIP_CONSTANTS } from "@Constants/common";
import {
  addToCartEvent,
  removeFromCartEvent,
  structuredTotalPrice,
  updateCartEvent,
} from "@Libs/dynamic-yield/trackingEvents";
import {
  IHandleDecrementalQty,
  IHandleIncrementalQty,
  ICartUtilQtyUpdate,
  ICartUtilRemoveItem,
} from "@Types/cartUtils";
import { convertToDollar } from "@Utils/currency";
import { GTMAddToCart, GTMRemoveFromCart } from "@Utils/gtm";
import { addToCartApi } from "@wff/api/cart/addToCartApi";
import { removeFromCartApi } from "@wff/api/cart/removeFromCartApi";
import { updateCartQtyApi } from "@wff/api/cart/updateCartQtyApi";
import { ICartLineItem } from "@wff/interfaces";
import { AppDispatch } from "@wff/store";
import { setCartIdHandler, setCartFromApiResponse } from "@wff/store/cartSlice";
import { cartToastActions } from "@wff/store/cartToastSlice";
import { toggleZipCodePopUp } from "@wff/store/zipcodeSlice";
import { findErrorCode } from "./errors";

export const showCartToast = (
  lineItems: ICartLineItem[],
  sku: string,
  dispatch: AppDispatch
) => {
  if (!lineItems.length) return;

  const itemIndex = getItemIndex(lineItems, sku);
  if (itemIndex < 0 || lineItems[itemIndex]?.quantity > 1) {
    return;
  }

  dispatch(cartToastActions.setToastItem(getLineItem(lineItems, itemIndex)));
};
export const displayErrMsg = (error: any, dispatch: AppDispatch) => {
  const errorCode = error?.response?.data?.errors?.[0]?.code;
  const errorMsg =
    findErrorCode({
      code: errorCode,
      error,
    }) || error;
  showErrorMsg(errorMsg, dispatch);
};

// Handles the increment of qty of an item in cart
export const cartUtilQtyAdd = async (
  cartId: string,
  payload: IHandleIncrementalQty
) => {
  const { itemIndex, itemQuantity, availability, sku, callback, dispatch } =
    payload;

  if (itemQuantity && itemQuantity >= availability) return;

  try {
    const res = await addToCartApi({ sku: sku, quantity: 1 });

    // Track `Add to Cart` Event for DY
    const { lineItems } = res?.data;
    const productIndex = itemIndex < 0 ? lineItems?.length - 1 : itemIndex;
    const { quantity, totalPrice } = lineItems[productIndex];
    const itemPrice = structuredTotalPrice(quantity, totalPrice);
    addToCartEvent({
      sku,
      quantity: 1,
      totalPrice: itemPrice,
      lineItems: lineItems,
    });
    // Track `Add to Cart` Event for DY Ends

    // Track `Add to Cart` Event for DataLayer
    GTMAddToCart({
      quantity: 1,
      itemPrice: itemPrice,
      totalPrice: itemPrice,
      lineItem: lineItems[productIndex],
    });
    // Track `Add to Cart` Event for DataLayer Ends

    // Fix the bug: CE-338: Preventing of adding more than one Membership to the cart
    const membershipInfo = res?.data?.lineItems?.filter(
      (item) => item.sku === MEMBERSHIP_CONSTANTS.MEMBERSHIP
    );
    if (membershipInfo[0]?.quantity > 1) {
      membershipInfo[0].quantity = 1;
    }

    // the initial check from the response is to prevent wiping an assigned Redux cartId with an empty orderNumber response
    // and then (when we know orderNumber response is Not empty) if the values are different we dispatch
    if (res?.data?.orderNumber) {
      if (cartId !== res?.data?.orderNumber) {
        dispatch(setCartIdHandler(res?.data?.orderNumber));
      }

      showCartToast(res.data.lineItems as ICartLineItem[], sku, dispatch);
    }
    callback(getItemQty(res.data.lineItems, itemIndex));
    dispatch(setCartFromApiResponse(res?.data));
  } catch (error: any) {
    displayErrMsg(error, dispatch);
    callback(null);
  }
};

// Handles the decrement of qty of an item in cart
export const cartUtilQtyRemove = async (payload: IHandleDecrementalQty) => {
  const { itemQuantity, lineItems, sku, callback, dispatch } = payload;
  if (itemQuantity! > 1) {
    const itemIndex = getItemIndex(lineItems, sku);
    const lineItemId = getLineItemId(lineItems, itemIndex);
    try {
      const res = await updateCartQtyApi({
        lineItemId: lineItemId,
        sku: sku,
        quantity: itemQuantity! - 1,
      });

      // Track `Remove from Cart` Event for DY
      const { lineItems } = res?.data;
      const { quantity, totalPrice } = lineItems[itemIndex];
      const itemPrice = structuredTotalPrice(quantity, totalPrice);

      removeFromCartEvent({
        sku,
        quantity: 1,
        totalPrice: itemPrice,
        lineItems,
      });
      // Track `Remove from Cart` Event for DY Ends

      // Track `Remove from Cart` Event for DataLayer
      GTMRemoveFromCart({
        quantity: 1,
        itemPrice: itemPrice,
        totalPrice: itemPrice,
        lineItem: lineItems[itemIndex],
      });
      // Track `Remove from Cart` Event for DataLayer Ends

      callback(getItemQty(res.data.lineItems, itemIndex));
      dispatch(setCartFromApiResponse(res?.data));
    } catch (error: any) {
      displayErrMsg(error, dispatch);
      callback(null);
    }
  } else if (itemQuantity === 1)
    cartUtilRemoveItem({ lineItems, sku, dispatch });
};

// Handles the update of qty of an item in cart
export const cartUtilQtyUpdate = async (payload: ICartUtilQtyUpdate) => {
  const {
    itemQuantity,
    prevQuantity,
    availability,
    lineItems,
    sku,
    dyQtyChangeEventPrevValue,
    callback,
    dispatch,
  } = payload;
  if (!itemQuantity || (itemQuantity && isNaN(itemQuantity)))
    return callback(prevQuantity);
  const itemIndex = getItemIndex(lineItems, sku);
  const newQty =
    itemQuantity && Number(itemQuantity) >= availability
      ? availability
      : itemQuantity;
  if (newQty === 0) return cartUtilRemoveItem({ lineItems, sku, dispatch });
  const lineItemId = getLineItemId(lineItems, itemIndex);
  try {
    const res = await updateCartQtyApi({
      lineItemId: lineItemId,
      sku: sku,
      quantity: newQty,
    });

    // 'Quantity Change' event for DY
    const cartLineItems = res.data.lineItems as ICartLineItem[];
    const { totalPrice } = cartLineItems[itemIndex];
    const itemPrice = structuredTotalPrice(itemQuantity, totalPrice);
    const dyQty = dyQtyChangeEventPrevValue ? itemQuantity : 1;
    const dyPrice = dyQtyChangeEventPrevValue ? totalPrice : itemPrice;

    updateCartEvent({
      sku,
      quantity: dyQty,
      prevQuantity: Number(dyQtyChangeEventPrevValue) || 0,
      totalPrice: dyPrice,
      lineItems: cartLineItems,
    });
    //'Quantity Change' event for DY Ends

    // Track `Update to Cart` Event for DataLayer
    GTMAddToCart({
      quantity: newQty,
      itemPrice: itemPrice,
      totalPrice: convertToDollar(totalPrice),
      lineItem: lineItems[itemIndex],
    });
    // Track `Update to Cart` Event for DataLayer Ends

    callback(getItemQty(res.data.lineItems, itemIndex));
    dispatch(setCartFromApiResponse(res?.data));
  } catch (error: any) {
    displayErrMsg(error, dispatch);
    callback(null);
  }
};

// Handles the full removal of an item in cart
export const cartUtilRemoveItem = async (payload: ICartUtilRemoveItem) => {
  const { lineItems, sku, dispatch } = payload;
  const itemIndex = getItemIndex(lineItems, sku);
  const lineItemId = getLineItemId(lineItems, itemIndex);
  try {
    const res = await removeFromCartApi({
      lineItemId: lineItemId,
    });

    // Track `Remove from Cart` Event for DY
    const { quantity, totalPrice } = lineItems[itemIndex];
    const itemPrice = structuredTotalPrice(1, totalPrice);
    const updatedLineItems = res?.data?.lineItems;

    removeFromCartEvent({
      sku: sku,
      quantity: quantity,
      totalPrice: itemPrice,
      lineItems: updatedLineItems,
    });
    // Track `Remove from Cart` Event for DY Ends

    // // Track `Remove from Cart` Event for DataLayer
    GTMRemoveFromCart({
      quantity: quantity,
      itemPrice: itemPrice,
      totalPrice: convertToDollar(totalPrice),
      lineItem: lineItems[itemIndex],
    });
    // // Track `Remove from Cart` Event for DataLayer Ends

    dispatch(setCartFromApiResponse(res?.data));
  } catch (error: any) {
    showErrorMsg(error, dispatch);
  }
};

// handles the update of the qty of an item in cart upon lost focus in qty input
export const cartUtilQtyOnBlurHandler = (payload: ICartUtilQtyUpdate) => {
  const { itemQuantity, prevQuantity, callback } = payload;
  if (itemQuantity) {
    setTimeout(() => {
      cartUtilQtyUpdate(payload);
    }, 10);
  } else callback(prevQuantity);
};

// <--- cartUtils for ButtonCartAddOrUpdate --->
export const structureCartUtils = (
  cartStore: any,
  zipCodeStore: any,
  dispatch: AppDispatch
) => ({
  cartStore: cartStore,
  zipCodeStore: zipCodeStore,
  openZipCodePopUp: () => {
    dispatch(toggleZipCodePopUp());
  },
  cartUtilQtyAdd: cartUtilQtyAdd,
  cartUtilQtyOnBlurHandler: cartUtilQtyOnBlurHandler,
  cartUtilQtyRemove: cartUtilQtyRemove,
  cartUtilRemoveItem: cartUtilRemoveItem,
  dispatch: dispatch,
});
